import { assertIsNever, objectKeys } from "@cervval.js/common/src/tools/object.tools";
import { UnixTimestamp } from "@cervval.js/common/src/tools/timestamps.tools.definitions";
import { TODAY } from "@cervval.js/common/src/tools/timestamps.tools.factory";
import { diff } from "@cervval.js/common/src/tools/timestamps.tools.tsdate";
import { ID } from "@ehealth_tools/common/src/data/propertyTypes/property.types";
import { isPromise } from "../global/tools/data.tools";
import { AppLoginData } from "./appLoginData";
import { CoachDTO, PatientDTO, PatientInfosDTO, ProfessionalInfosDTO, UserDTO } from "./user.dto";
import { i18nUserTools } from "./user.tools.i18n";
import { DAYS_OF_WEEK, DAYS_OF_WEEKS, JOB_TYPE, USER_TYPE } from "./user.types";

/** Typeguard for coach */
export function isCoachDTO(user: UserDTO): user is CoachDTO {
    return (user as CoachDTO).userType === USER_TYPE.COACH;
}

/** Typeguard for patient */
export function isPatientDTO(user: UserDTO): user is PatientDTO {
    return (user as PatientDTO).userType === USER_TYPE.PATIENT;
}

/** */
export function isPatientInfos(infos: PatientInfosDTO | ProfessionalInfosDTO): infos is PatientInfosDTO {
    return (infos as PatientInfosDTO).inclusionDate != null;
}

/**
 * Return if a coach is a developer
 * @param user User profile we want to check
 * @param getInfos Function to use to get ProfessionalInfosDTO object linked to the given user
 */
export function checkIsDeveloper<P extends Promise<ProfessionalInfosDTO | null> | (ProfessionalInfosDTO | null), R extends P extends Promise<ProfessionalInfosDTO | null> ? Promise<boolean> : boolean>(user: UserDTO, getInfos: (userId: ID) => P): R {
    const pInfo = getInfos(user.id);
    if (isPromise(pInfo)) {
        return pInfo.then(r => r?.jobType === JOB_TYPE.DEV) as R;

    } else {
        return (pInfo?.jobType === JOB_TYPE.DEV) as R;
    }
}

/** Sorted keys for UX consistency */
export const coachKeysSorted: (keyof CoachDTO)[] = ["givenName", "familyName", "phoneNumber", "email", "creationDate"];
/** Sorted keys of `PatientDTO`, that can be used to order field display in forms */
export const patientKeysSorted: (keyof PatientDTO)[] = ["gender", "givenName", "familyName", "birthDate", "phoneNumber", "email", "address", "postalCode", "city"];


/** */
export function getPatientAge(patient: PatientDTO): number {
    const now = TODAY();
    const age = Math.floor(diff(patient.birthDate, now, "year"));
    return age;
}


export const allWeekAvailability: DAYS_OF_WEEKS = [DAYS_OF_WEEK.MONDAY, DAYS_OF_WEEK.TUESDAY, DAYS_OF_WEEK.WEDNESDAY, DAYS_OF_WEEK.THURSDAY, DAYS_OF_WEEK.FRIDAY, DAYS_OF_WEEK.SATURDAY, DAYS_OF_WEEK.SUNDAY];

/**
 * Nullify user authentication information :
 * remove password-related information or auth token if possible, otherwise set it to an empty / falsy value
 * @param user UserDTO object
 */
export function nullifyUserAuthInformation(user: UserDTO): void {
    user.notificationToken = undefined;
    user.password = undefined;
    user.passwordResetRequired = false;
    user.resetPasswordKey = "";
    user.resetPasswordKeyGenerationTime = 0 as unknown as UnixTimestamp;
}

/** !! THIS MUST MATCH DB.id */
export enum RoleType {
    /** Developer user */
    DEV = 1,
    /** Patient user */
    PATIENT = 2,
    /** Professional user, with employee position (contextual to a given company) */
    EMPLOYEE = 3,
    /** Professional user, with manager position (contextual to a given company) */
    MANAGER = 4,
    /** Professional user, within a company with completed information, i.e created in db and stripe (contextual to a given company, build for login data) */
    PROFILE_COMPLETE = 5,
    /** Professional user, within a company with active subscription (contextual to a given company, build for login data) */
    HAS_PAID = 6
}

export const RoleTypeValues = new Set(Object.values(RoleType));

/** */
export function getRoleTypeI18n(type: RoleType): string {
    const i18n = i18nUserTools().roleType;
    switch (type) {
        case RoleType.DEV: return i18n.dev;
        case RoleType.PATIENT: return i18n.patient;
        case RoleType.EMPLOYEE: return i18n.employee;
        case RoleType.MANAGER: return i18n.manager;
        case RoleType.PROFILE_COMPLETE: return i18n.profileComplete;
        case RoleType.HAS_PAID: return i18n.hasPaid;
        default:
            assertIsNever(type);
            return "NA";
    }
}

/**
 * Check if a user has a given contextual role based on its login data.
 * The search is performed on the `contextRoles` object of the login data, and will return true at the first encounter of the given role within a company context
 * @param loginData `AppLoginData` of a user
 * @param role `RoleType` we want to check
 * @returns `true` if we found `role` for a context in user's `loginData`, `false` otherwise
 */
export function doesUserHaveContextualRole(loginData: AppLoginData, role: RoleType): boolean {
    if (loginData.contextRoles == null || loginData.contextRoles.companyId == null) {
        return false;
    }
    for (const key of objectKeys(loginData.contextRoles.companyId)) {
        const contextualRolesOfCompany = loginData.contextRoles.companyId[key];
        if (contextualRolesOfCompany.find((r) => r.id === role) != null) {
            return true;
        }
    }
    return false;
}